import { getLocale } from '@tov-plus/ui';
import { useMemo } from 'react';
import { NavLink } from '@tov-plus/frontend-data';
import { useTovQuery } from '@tov-plus/frontend-data';
import { CachedDataNotice } from '@tov-plus/pwa';
import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { useGuestLiveUpdates } from '../lib/communications/useGuestLiveUpdates';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import { formatGuestDateTime } from '../lib/localization/guestLocale';
import { msg, TovCard } from '@tov-plus/ui';
import {
  readGuestSnapshot,
  writeGuestSnapshot,
} from '../lib/offline/cacheSnapshot';
function getTodayCacheKey(occasionSlug: string, guestToken: string) {
  return `tov-plus:guest-today:${occasionSlug}:${guestToken}`;
}
function isEventDay(timestamp: string | null) {
  if (!timestamp) {
    return false;
  }
  const delta = Date.parse(timestamp) - Date.now();
  return delta >= -1000 * 60 * 60 * 12 && delta <= 1000 * 60 * 60 * 36;
}

const todayErrorMessage = () =>
  msg('pages.today_page.event_day_view_could_not_be_loaded');

export function TodayPage() {
  const access = useGuestAccess();
  const liveUpdates = useGuestLiveUpdates(access);
  const readyOccasionSlug =
    access.status === 'ready' ? access.experience.occasion.slug : null;
  const readyGuestToken = access.status === 'ready' ? access.guestToken : null;
  const todayQuery = useTovQuery({
    queryKey:
      readyOccasionSlug && readyGuestToken
        ? guestQueryKeys.today(readyOccasionSlug, readyGuestToken)
        : guestQueryKeys.today('pending', 'pending'),
    queryFn: async () => {
      const occasionSlug = readyOccasionSlug!;
      const guestToken = readyGuestToken!;
      const cacheKey = getTodayCacheKey(occasionSlug, guestToken);
      try {
        const [timelineResponse, locationsResponse] = await Promise.all([
          guestApi.listTimelineSummary(occasionSlug, guestToken),
          guestApi.listVisibleLocations(occasionSlug, guestToken),
        ]);
        await writeGuestSnapshot(cacheKey, {
          scheduleItems: timelineResponse,
          locations: locationsResponse,
          cachedAt: new Date().toISOString(),
        });
        return {
          scheduleItems: timelineResponse,
          locations: locationsResponse,
          cached: false,
        };
      } catch (error) {
        const cached = await readGuestSnapshot<{
          scheduleItems: Awaited<
            ReturnType<typeof guestApi.listTimelineSummary>
          >;
          locations: Awaited<ReturnType<typeof guestApi.listVisibleLocations>>;
        }>(cacheKey);
        if (cached?.scheduleItems && cached.locations) {
          return {
            scheduleItems: cached.scheduleItems,
            locations: cached.locations,
            cached: true,
          };
        }
        throw error;
      }
    },
    enabled: Boolean(readyOccasionSlug && readyGuestToken),
  });
  const scheduleItems = todayQuery.data?.scheduleItems ?? [];
  const locations = todayQuery.data?.locations ?? null;
  const errorMessage = todayQuery.data?.cached
    ? 'Offline cache loaded. Some live event-day details may be out of date.'
    : todayQuery.isError
      ? getGuestErrorMessage(todayQuery.error, todayErrorMessage())
      : null;
  const eventDayEvents = useMemo(() => {
    if (access.status !== 'ready') {
      return [];
    }
    return access.experience.events.filter((event) =>
      isEventDay(event.startsAt),
    );
  }, [access]);
  const eventDayItems = useMemo(
    () =>
      scheduleItems.filter((item) => isEventDay(item.timelineItem.startsAt)),
    [scheduleItems],
  );
  const transportLocations = useMemo(
    () =>
      (locations?.locations ?? []).filter((location) =>
        ['parking', 'transportation_point', 'accommodation', 'venue'].includes(
          location.type,
        ),
      ),
    [locations],
  );
  const travelContacts = useMemo(
    () =>
      (locations?.contacts ?? []).filter((contact) =>
        transportLocations.some(
          (location) => location.id === contact.location_id,
        ),
      ),
    [locations, transportLocations],
  );
  const latestUpdates = useMemo(
    () => liveUpdates.unreadItems.slice(0, 4),
    [liveUpdates.unreadItems],
  );
  if (access.status !== 'ready' || todayQuery.isLoading) {
    return <StatePanel preset="loading" />;
  }
  if (todayQuery.isError) {
    return <StatePanel preset="loading" />;
  }
  const resolvedLocaleCode = getLocale();
  const occasionTimezone = access.experience.occasion.timezone;
  return (
    <div className="page-grid">
      {errorMessage ? (
        <TovCard variant="content" isWide>
          <p className="page-eyebrow">
            {msg('pages.today_page.pages_today_page_offline_continuity')}
          </p>
          <p>{errorMessage}</p>
        </TovCard>
      ) : null}
      <CachedDataNotice cachedValue={locations ?? scheduleItems} />
      <TovCard variant="content" isWide className="hero-card">
        <p className="page-eyebrow">{msg('general.calendar.today')}</p>
        <h2>
          {msg(
            'pages.today_page.pages_today_page_guest_event_day_command_view',
          )}
        </h2>
        <p>
          {msg(
            'pages.today_page.pages_today_page_this_page_pulls_the_guest_safe_parts_of',
          )}{' '}
        </p>
        <div className="hero-card__stats">
          <article>
            <strong>{eventDayEvents.length}</strong>
            <span>
              {msg('pages.today_page.pages_today_page_event_day_event')}
            </span>
          </article>
          <article>
            <strong>{eventDayItems.length}</strong>
            <span>
              {msg('pages.today_page.pages_today_page_today_schedule_item')}
            </span>
          </article>
          <article>
            <strong>{latestUpdates.length}</strong>
            <span>
              {msg('pages.today_page.pages_today_page_unread_live_update')}
            </span>
          </article>
          <article>
            <strong>{travelContacts.length}</strong>
            <span>
              {msg('pages.today_page.pages_today_page_travel_contact')}
            </span>
          </article>
        </div>
        <div className="action-row">
          <NavLink className="primary-button" to="/schedule">
            {msg('pages.today_page.pages_today_page_full_schedule')}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/locations">
            {msg('pages.today_page.pages_today_page_travel_guidance')}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/travel-packet">
            {msg('pages.today_page.pages_today_page_travel_packet')}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/messages">
            {msg('pages.today_page.pages_today_page_message_center')}{' '}
          </NavLink>
        </div>
      </TovCard>

      <TovCard variant="content">
        <p className="page-eyebrow">
          {msg('pages.today_page.pages_today_page_arrivals')}
        </p>
        {transportLocations.length > 0 ? (
          <div className="detail-list">
            {transportLocations.slice(0, 4).map((location) => (
              <article key={location.id}>
                <strong>{location.title}</strong>
                <span>
                  {location.addressText ??
                    msg('general.states.address_pending')}
                </span>
                <span>{location.type.replaceAll('_', ' ')}</span>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.today_page.pages_today_page_no_event_day_arrival_guidance_has_been_published',
            )}
          </p>
        )}
      </TovCard>

      <TovCard variant="content">
        <p className="page-eyebrow">
          {msg('pages.today_page.pages_today_page_travel_contacts')}
        </p>
        {travelContacts.length > 0 ? (
          <div className="detail-list">
            {travelContacts.slice(0, 4).map((contact) => (
              <article key={contact.id}>
                <strong>{contact.label}</strong>
                <span>{contact.value}</span>
                <span>
                  {contact.notes ??
                    msg(
                      'pages.today_page.pages_today_page_use_this_contact_for_event_day_travel_help',
                    )}
                </span>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.today_page.pages_today_page_no_host_provided_travel_contacts_are_attached_to',
            )}{' '}
          </p>
        )}
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.today_page.pages_today_page_live_changes')}
        </p>
        {latestUpdates.length > 0 ? (
          <div className="detail-list detail-list--stacked">
            {latestUpdates.map((item) => (
              <article key={item.delivery.id}>
                <strong>
                  {item.message?.subjectText ??
                    item.event?.displayName ??
                    msg('pages.help_page.pages_help_page_occasion_update')}
                </strong>
                <span>
                  {msg('components.shell.sent')}{' '}
                  {formatGuestDateTime(
                    item.delivery.queuedAt,
                    resolvedLocaleCode,
                    occasionTimezone,
                  )}
                  {item.event ? ` • ${item.event.displayName}` : ''}
                </span>
                <span>
                  {item.message?.bodyText ??
                    msg(
                      'pages.today_page.pages_today_page_open_the_linked_guest_route_for_the_latest_details',
                    )}
                </span>
                <div className="action-row">
                  <NavLink
                    className="secondary-link"
                    to={item.deepLink ?? '/messages'}
                  >
                    {msg(
                      'pages.today_page.pages_today_page_open_linked_surface',
                    )}{' '}
                  </NavLink>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.today_page.pages_today_page_no_unread_event_day_updates_are_waiting_right',
            )}
          </p>
        )}
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.today_page.pages_today_page_today_timeline')}
        </p>
        {eventDayItems.length > 0 ? (
          <div className="detail-list detail-list--stacked">
            {eventDayItems.map((item) => (
              <article key={item.timelineItem.id}>
                <strong>{item.timelineItem.title}</strong>
                <span>
                  {item.event.displayName} •{' '}
                  {formatGuestDateTime(
                    item.timelineItem.startsAt,
                    getLocale(),
                    item.event.timezone ?? access.experience.occasion.timezone,
                  )}
                </span>
                <span>
                  {item.timelineItem.description ??
                    item.event.guestSummary ??
                    msg(
                      'pages.today_page.pages_today_page_no_extra_guest_summary_provided',
                    )}
                </span>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.today_page.pages_today_page_no_timeline_items_currently_fall_inside_the_event',
            )}
          </p>
        )}
      </TovCard>
    </div>
  );
}
